import {
  EavEntityFormDto,
  type EavEntityFormInterface,
} from '@zaoblako/shared/modules/eav/dtos/entity/eav-entity-form.dto';
import { entityLikeSchema } from '@zaoblako/shared/modules/serde/schemas/entity-like.schema';
import type { DealsGroupFindInterface } from '@zaoblako/shared/modules/business/dto/deals-group/deals-group-find.dto';
import type { ContragentFindInterface } from '@zaoblako/shared/modules/client/dtos/contragent/contragent-find.dto';
import type { UserFindInterface } from '@zaoblako/shared/modules/security/dtos/user/user-find.dto';
import type { UserGroupFindInterface } from '@zaoblako/shared/modules/security/dtos/user-group/user-group-find.dto';
import type { CompanyFindInterface } from '@zaoblako/shared/modules/client/dtos/company/find/company-find.dto';
import type { DirectionFindInterface } from '@zaoblako/shared/modules/catalog/dtos/direction/direction-find.dto';
import type { UnitFindInterface } from '@zaoblako/shared/modules/catalog/dtos/unit/unit-find.dto';
import type { FilialFindInterface } from '@zaoblako/shared/modules/catalog/dtos/filial/filial-find.dto';
import type { ProjectFindInterface } from '@zaoblako/shared/modules/project/dto/project/project-find.dto';
import type { HelpdeskTaskFindInterface } from '@zaoblako/shared/modules/helpdesk/dto/helpdesk-task-find.dto';
import type { ContractFindInterface } from '@zaoblako/shared/modules/document/dtos/contract/contract-find.dto';
import type {
  ExternalUserFindInterface,
} from '@zaoblako/shared/modules/security/dtos/external-user/external-user-find.dto';
import type { DealTypeFindInterface } from '@zaoblako/shared/modules/business/dto/deal-type/deal-type.find.dto';
import type { RoomFindInterface } from '@zaoblako/shared/modules/catalog/dtos/room/room-find.dto';
import {
  DealOrderFormDto,
  type DealOrderFormInterface,
} from '@zaoblako/shared/modules/business/dto/deal-order/deal-order-form.dto';
import { HelpdeskTaskPriorityEnum } from '@zaoblako/shared/modules/helpdesk/enum/helpdesk.task.priority.enum';
import { HelpdeskPriorityI18nEnum } from '@zaoblako/shared/modules/helpdesk/i18n/helpdesk.priority.i18n.enum';
import { DtoMessageEnum } from '@zaoblako/shared/common/enums/dto.message.enum';
import { BusinessDtoKeysEnum } from '@zaoblako/shared/modules/business/enum/business-dto-keys.enum';
import { SerdeDtoClass } from '@zaoblako/shared/modules/serde/classes/serde-dto.class';
import { commonEnumValidator } from '@zaoblako/shared/modules/serde/validators/common-enum.validator';
import { CatalogCheckersEnum } from '@zaoblako/shared/modules/catalog/enums/catalog-checkers.enum';

import * as yup from 'yup';

/** Deal creation and updating requests body */
export interface DealFormInterface {
  /** Deal display name */
  name: string;
  /** Deal shipping date and time */
  shippingDate?: Date;
  /** Project priority ({@link HelpdeskTaskPriorityEnum.MEDIUM} by default) */
  priority?: HelpdeskTaskPriorityEnum;
  plannedMin?: number;
  /** Deal commentary (just some text, maybe formatted) */
  comment?: string;
  /** Deal's counterparty */
  contragent: ContragentFindInterface;
  /** Deal's company */
  company?: CompanyFindInterface;
  /** Deal's direction */
  direction?: DirectionFindInterface;
  /** Deal's unit */
  unit?: UnitFindInterface;
  /** Deal's filial */
  filial?: FilialFindInterface;
  /** Deal's contract */
  contract?: ContractFindInterface;
  /** Group this deal belongs to (optional) */
  group?: DealsGroupFindInterface;
  /** Deal type */
  dealType?: DealTypeFindInterface;
  /** User auditors (deals group participants) */
  userAuditors?: UserFindInterface[];
  /** Group auditors (deals group participants) */
  groupAuditors?: UserGroupFindInterface[];
  createdBy?: UserFindInterface;
  responsible?: UserFindInterface;
  orders: DealOrderFormInterface[];
  /** Projects to associate with deal */
  projects?: ProjectFindInterface[];
  /** HelpDesk tasks to associate with deal */
  helpdeskTasks?: HelpdeskTaskFindInterface[];
  /** External user relation */
  externalUser?: ExternalUserFindInterface;
  /** Deal plan amount in roubles */
  plan?: number;
  /** EAV entity with additional data */
  eavEntity?: EavEntityFormInterface;
  // Rent columns
  /** Rent: rented premises */
  rentRooms?: RoomFindInterface[];
  /** Rent: lease start date */
  rentStartDate?: Date;
  /** Rent: corridor factor */
  rentCorridorFactor?: number;
  /** Rent: security deposit */
  rentSecurityDeposit?: number;
  /** Rent: parking lot price */
  rentParkingLotPrice?: number;
  /** Rent amount (computed by other rent fields) */
  rentAmount?: number;
}

/** Deal creation and updating requests body - DTO schema */
export const DealFormDto = SerdeDtoClass.create<DealFormInterface>(BusinessDtoKeysEnum.DEAL_FORM_DTO, {
  name: {
    label: 'Название',
    serializer: yup.string(),
  },
  shippingDate: {
    optional: true,
    label: 'Дата',
    serializer: yup.date(),
  },
  plan: {
    optional: true,
    label: 'План',
    serializer: yup.number(),
  },
  priority: {
    optional: true,
    label: 'Приоритет',
    serializer: commonEnumValidator(HelpdeskTaskPriorityEnum, HelpdeskPriorityI18nEnum),
  },
  plannedMin: {
    optional: true,
    label: 'Запланированное время',
    serializer: yup
      .number()
      .integer(DtoMessageEnum.NOT_INTEGER)
      .min(0, 'Запланированное время в минутах не может быть отрицательным'),
  },
  comment: {
    optional: true,
    label: 'Комментарий',
    serializer: yup.string(),
  },
  contragent: {
    optional: true,
    label: 'Контрагент',
    serializer: entityLikeSchema,
  },
  company: {
    optional: true,
    label: 'Компания',
    serializer: entityLikeSchema,
  },
  direction: {
    optional: true,
    label: 'БС',
    serializer: entityLikeSchema,
  },
  unit: {
    optional: true,
    label: 'БУ',
    serializer: entityLikeSchema,
  },
  externalUser: {
    optional: true,
    label: 'Внешний пользователь',
    serializer: entityLikeSchema,
  },
  filial: {
    optional: true,
    label: 'Филиал',
    serializer: entityLikeSchema,
  },
  contract: {
    optional: true,
    label: 'Договор',
    serializer: entityLikeSchema,
  },
  group: {
    optional: true,
    label: 'Группа',
    serializer: entityLikeSchema,
  },
  dealType: {
    optional: true,
    label: 'Тип',
    serializer: entityLikeSchema,
  },
  userAuditors: {
    optional: true,
    label: 'Аудиторы',
    serializer: yup.array(entityLikeSchema),
  },
  groupAuditors: {
    optional: true,
    label: 'Группы аудиторов',
    serializer: yup.array(entityLikeSchema),
  },
  createdBy: {
    optional: true,
    label: 'Создатель',
    serializer: entityLikeSchema,
  },
  responsible: {
    optional: true,
    label: 'Ответственный',
    serializer: entityLikeSchema,
  },
  orders: {
    label: 'Заказы',
    nested: DealOrderFormDto,
    serializer: (_, nested) => yup.array(nested),
  },
  projects: {
    optional: true,
    label: 'Проекты',
    serializer: yup.array(entityLikeSchema),
  },
  helpdeskTasks: {
    optional: true,
    label: 'Задачи HD',
    serializer: yup.array(entityLikeSchema),
  },
  eavEntity: {
    optional: true,
    label: 'Дополнительные реквизиты',
    nested: EavEntityFormDto,
    serializer: (_, nested) => nested,
  },
  rentRooms: {
    optional: true,
    label: 'Аренда: Помещения',
    serializer: yup.array(entityLikeSchema),
    checkers: [
      CatalogCheckersEnum.ROOM_ASSERT_EXISTS_BY_DTO,
      CatalogCheckersEnum.ROOM_ASSERT_ONE_TYPE_BY_DTO,
    ],
  },
  rentStartDate: {
    optional: true,
    label: 'Аренда: Дата начала аренды',
    serializer: yup.date(),
  },
  rentCorridorFactor: {
    optional: true,
    label: 'Аренда: Коридорный коэффициент',
    serializer: yup
      .number()
      .min(0, DtoMessageEnum.NOT_ZERO_OR_POSITIVE),
  },
  rentSecurityDeposit: {
    optional: true,
    label: 'Аренда: Обеспечительный взнос',
    serializer: yup
      .number()
      .min(0, DtoMessageEnum.NOT_ZERO_OR_POSITIVE),
  },
  rentParkingLotPrice: {
    optional: true,
    label: 'Аренда: стоимость парковочного места',
    serializer: yup
      .number()
      .min(0, DtoMessageEnum.NOT_ZERO_OR_POSITIVE),
  },
  rentAmount: {
    optional: true,
    label: 'Аренда: Арендная плата',
    serializer: yup.number(),
  },
});
